import { AccountPlatformType, AccountType } from "shared/types/accounts";

type AccountPlatformFields = {
  platformType?: AccountPlatformType;
};

export function getAccountPlatformType(
  account?: AccountPlatformFields
): AccountPlatformType {
  return account?.platformType || "IG";
}

export function isInstagramAccount(account: AccountType): boolean {
  return getAccountPlatformType(account) === "IG";
}

export function isLinkedInAccount(account: AccountType): boolean {
  return getAccountPlatformType(account) === "LI";
}

/**
 * True if the LinkedIn account is a Company / Organization page.
 *
 * Discriminator: account.linkedinUrn looks like "urn:li:organization:..." for
 * companies and "urn:li:person:..." for personal profiles. Match the prefix
 * exactly so a stray "organization" substring elsewhere in a URN can't flip
 * a personal account to company.
 */
export function isLinkedinCompanyAccount(account: AccountType): boolean {
  if (!isLinkedInAccount(account)) return false;
  return account.linkedinUrn?.startsWith("urn:li:organization:") ?? false;
}

/**
 * True if the LinkedIn account is a Personal profile.
 *
 * Defaults to "personal" when the URN is missing — this prevents the connect
 * flow from accidentally allowing two personal accounts during the brief
 * window between connection and URN hydration.
 */
export function isLinkedinPersonalAccount(account: AccountType): boolean {
  if (!isLinkedInAccount(account)) return false;
  return !isLinkedinCompanyAccount(account);
}

export function isAccountFacebookPage(account: AccountType): boolean {
  return getAccountPlatformType(account) === "FB" && !account.isGroup;
}

export function showInsightsEmails(account: AccountType): boolean {
  return (
    isAccountFacebookPage(account) ||
    isLinkedInAccount(account) ||
    isInstagramAccount(account)
  );
}

export function getPFItemDesc(account: AccountType): string {
  if (account.isGroup === true) {
    return "Group";
  }
  switch (getAccountPlatformType(account)) {
    case "FB":
      return "Page";
    case "LI":
      return "LinkedIn Page";
    case "TW":
      return "account";
    case "GMB":
      return "location";
    case "YT":
      return "channel";
    default:
      // PI, TT, SC, OT
      return "account";
  }
}
